import { useCallback, useEffect, useState } from 'react';

export function distanceToSite(fix, site) {
  if (!fix || site?.lat == null || site?.lng == null) return null;
  const rad = (n) => n * Math.PI / 180;
  const a = Math.sin(rad(site.lat - fix.lat) / 2) ** 2
    + Math.cos(rad(fix.lat)) * Math.cos(rad(site.lat)) * Math.sin(rad(site.lng - fix.lng) / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
const OPTIONS = { enableHighAccuracy: true, maximumAge: 10000, timeout: 60000 };
export default function useGeoFix(enabled = true) {
  const [fix, setFix] = useState(null);
  const [status, setStatus] = useState('searching');
  const success = useCallback((position) => {
    setFix({ lat: position.coords.latitude, lng: position.coords.longitude, accuracy_m: position.coords.accuracy, fix_at: position.timestamp });
    setStatus('fix');
  }, []);
  const failure = useCallback((error) => {
    // A timeout is not a revocation of an acquired fix. Long suspensions expire it below.
    if (error.code === 3) { setStatus((previous) => previous === 'fix' ? 'fix' : 'unavailable'); return; }
    setStatus(error.code === 1 ? 'denied' : 'unavailable'); setFix(null);
  }, []);
  const retry = useCallback(() => {
    setStatus('searching');
    if (navigator.geolocation) navigator.geolocation.getCurrentPosition(success, failure, OPTIONS);
    else setStatus('unavailable');
  }, [success, failure]);
  useEffect(() => {
    if (!enabled) return undefined;
    let watch;
    let hiddenAt = null;
    let epoch = 0;
    const stop = () => { epoch += 1; if (watch != null) navigator.geolocation?.clearWatch(watch); watch = null; };
    const start = () => {
      stop();
      if (document.visibilityState === 'hidden') { hiddenAt ??= Date.now(); return; }
      // Keep a short native-camera round trip, but never reuse a previous site's long-suspended fix.
      if (hiddenAt != null && Date.now() - hiddenAt > 10 * 60000) { setFix(null); setStatus('searching'); }
      hiddenAt = null;
      if (!navigator.geolocation) { setStatus('unavailable'); return; }
      const activeEpoch = epoch;
      watch = navigator.geolocation.watchPosition(
        (position) => { if (epoch === activeEpoch) success(position); },
        (error) => { if (epoch === activeEpoch) failure(error); }, OPTIONS);
    };
    start();
    document.addEventListener('visibilitychange', start);
    return () => { stop(); document.removeEventListener('visibilitychange', start); };
  }, [enabled, success, failure]);
  return { fix, status, retry };
}
